import { isAxiosError } from "axios";
import { apiClient } from "./api-client";
import type {
  CmeMemberHoursSource,
  DynamicFieldsRequest,
  DynamicHoursRow,
  DynamicHoursSaveRow,
  EnergyHoursResponse,
  EnergyHoursSaveRow,
  EnergyStafferDealResourceResponse,
  EnergyStafferDealSaveRow,
  EnergyStafferResponse,
  PowerHoursResponse,
  PowerHoursSavePayload,
  RealEstateHoursSource,
  TechnologyHoursResponse,
  TechnologyHoursSaveRow,
  TupleHoursSource,
} from "@/components/Workflow/QuickActions/WeeklyHours/weekly-hours-types";

function messageFromResponse(value: unknown): string | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  if (record.status !== "dashboardNotAuthorized") return null;
  return typeof record.message === "string" ? record.message : "This dashboard form is not available for your account.";
}

function requireAuthorized<T>(value: unknown): T {
  const message = messageFromResponse(value);
  if (message) throw new Error(message);
  return value as T;
}

// The dashboard controllers return their "not authorized / not configured" explanations in
// response bodies on both 200 and 4xx statuses. Surface those messages instead of the
// generic axios "Request failed with status code…" text.
function serverMessage(reason: unknown): string | null {
  if (!isAxiosError(reason)) return null;
  const data: unknown = reason.response?.data;
  const notAuthorized = messageFromResponse(data);
  if (notAuthorized) return notAuthorized;
  if (typeof data === "string" && data.trim()) return data;
  return null;
}

async function requestAuthorized<T>(request: () => Promise<{ data: unknown }>): Promise<T> {
  try {
    const response = await request();
    return requireAuthorized<T>(response.data);
  } catch (reason: unknown) {
    const message = serverMessage(reason);
    if (message) throw new Error(message);
    throw reason;
  }
}

export const HoursApi = {
  getUserSubDepartments: async (): Promise<string[]> => {
    try {
      const response = await apiClient.get<unknown>("/dashboard/GetUserSubDepartments");
      if (!Array.isArray(response.data)) throw new Error("TRIYO did not return your dashboard access list.");
      return response.data.filter((value): value is string => typeof value === "string");
    } catch (reason: unknown) {
      if (isAxiosError(reason) && reason.response) {
        const { status } = reason.response;
        const message = serverMessage(reason);
        // 403 (no Dashboard.View privilege) and the 400 "not authorized" variant are the
        // API's permanent "this user has no dashboard" answers, not transient failures.
        if (status === 403 || (status === 400 && /not authorized/i.test(message ?? ""))) return [];
        if (message) throw new Error(message);
      }
      throw reason;
    }
  },

  getEnergyHours: async (isSi: boolean): Promise<EnergyHoursResponse> => {
    const route = isSi ? "/dashboard/energy/EnergySIBankerTimeLog" : "/dashboard/energy/EnergyBankerTimeLog";
    return requestAuthorized<EnergyHoursResponse>(() => apiClient.get<unknown>(route));
  },

  saveEnergyHours: async (isSi: boolean, rows: EnergyHoursSaveRow[]): Promise<EnergyHoursResponse> => {
    const route = isSi
      ? "/dashboard/energy/submitSIBankerTimeLogBulkEdit"
      : "/dashboard/energy/submitBankerTimeLogBulkEdit";
    return requestAuthorized<EnergyHoursResponse>(() => apiClient.post<unknown>(route, rows));
  },

  getTechnologyHours: async (): Promise<TechnologyHoursResponse> => {
    return requestAuthorized<TechnologyHoursResponse>(() =>
      apiClient.get<unknown>("/dashboard/tech/TechBankerTimeLog")
    );
  },

  saveTechnologyHours: async (rows: TechnologyHoursSaveRow[]): Promise<TechnologyHoursResponse> => {
    return requestAuthorized<TechnologyHoursResponse>(() =>
      apiClient.post<unknown>("/dashboard/tech/submit-tech-banker-hours-bulk-edit", rows)
    );
  },

  getPowerHours: async (): Promise<PowerHoursResponse> => {
    return requestAuthorized<PowerHoursResponse>(() => apiClient.get<unknown>("/dashboard/power/PowerBankerTimeLog"));
  },

  savePowerHours: async (payload: PowerHoursSavePayload): Promise<PowerHoursResponse> => {
    return requestAuthorized<PowerHoursResponse>(() =>
      apiClient.post<unknown>("/dashboard/power/submitPowerBankerHoursBulkEdit", payload)
    );
  },

  getEnergyStafferSummary: async (isSi: boolean): Promise<EnergyStafferResponse> => {
    const route = isSi ? "/dashboard/energy/EnergySIStafferTimeLog" : "/dashboard/energy/EnergyStafferTimeLog";
    return requestAuthorized<EnergyStafferResponse>(() => apiClient.get<unknown>(route));
  },

  getEnergyBankerForStaffer: async (isSi: boolean, analystId: number): Promise<EnergyHoursResponse> => {
    const route = isSi
      ? "/dashboard/energy/EnergySIBankerTimeLogForStaffer"
      : "/dashboard/energy/EnergyBankerTimeLogForStaffer";
    return requestAuthorized<EnergyHoursResponse>(() => apiClient.post<unknown>(route, { AnalystId: analystId }));
  },

  saveEnergyBankerForStaffer: async (isSi: boolean, rows: EnergyHoursSaveRow[]): Promise<EnergyHoursResponse> => {
    const route = isSi
      ? "/dashboard/energy/submitSIBankerTimeLogBulkEditForStaffer"
      : "/dashboard/energy/submitBankerTimeLogBulkEditForStaffer";
    return requestAuthorized<EnergyHoursResponse>(() => apiClient.post<unknown>(route, rows));
  },

  getEnergyStafferDealResourceLog: async (isSi: boolean): Promise<EnergyStafferDealResourceResponse> => {
    const route = isSi
      ? "/dashboard/energy/GetSIStafferDealDetailsandResourcingLog"
      : "/dashboard/energy/GetStafferDealDetailsandResourcingLog";
    return requestAuthorized<EnergyStafferDealResourceResponse>(() => apiClient.get<unknown>(route));
  },

  // The save action returns a bare boolean, not the resource log; callers refetch.
  saveEnergyStafferDealResourceLog: async (isSi: boolean, rows: EnergyStafferDealSaveRow[]): Promise<void> => {
    const route = isSi
      ? "/dashboard/energy/submitSIStafferResourceDealLogBulkEdit"
      : "/dashboard/energy/submitStafferResourceDealLogBulkEdit";
    await requestAuthorized<unknown>(() => apiClient.post<unknown>(route, rows));
  },

  getCmeMemberHoursSource: async (): Promise<CmeMemberHoursSource[]> => {
    return requestAuthorized<CmeMemberHoursSource[]>(() => apiClient.get<unknown>("/dashboard/cme/membertimelog"));
  },

  getConsumerRetailHoursSource: async (): Promise<TupleHoursSource> => {
    return requestAuthorized<TupleHoursSource>(() => apiClient.get<unknown>("/dashboard/ConsumerRetail/MemberTimeLog"));
  },

  getGpcHoursSource: async (): Promise<TupleHoursSource> => {
    return requestAuthorized<TupleHoursSource>(() => apiClient.get<unknown>("/dashboard/gpc/membertimelog"));
  },

  getRealEstateHoursSource: async (): Promise<RealEstateHoursSource> => {
    return requestAuthorized<RealEstateHoursSource>(() => apiClient.get<unknown>("/dashboard/realEstate?type="));
  },

  getDynamicHours: async (request: DynamicFieldsRequest): Promise<DynamicHoursRow[]> => {
    return requestAuthorized<DynamicHoursRow[]>(() =>
      apiClient.get<unknown>("/dynamic-fields-bulk-edit", {
        params: {
          objectType: request.objectType,
          objectId: request.objectId,
          taskTypeId: request.taskTypeId,
          projectTypeId: request.projectTypeId,
          taskIds: request.taskIds,
        },
        // Classic ASP.NET Web API binds List<int> from repeated keys (taskIds=1&taskIds=2),
        // not from a comma-joined value.
        paramsSerializer: { indexes: null },
      })
    );
  },

  getRealEstateDynamicHours: async (request: DynamicFieldsRequest): Promise<DynamicHoursRow[]> => {
    return requestAuthorized<DynamicHoursRow[]>(() => apiClient.post<unknown>("/dynamic-fields-bulk-edit-re", request));
  },

  saveDynamicHours: async (rows: DynamicHoursSaveRow[]): Promise<void> => {
    await requestAuthorized<unknown>(() => apiClient.post("/dynamic-fields-bulk-edit", rows));
  },

  carryOverDashboardData: async (): Promise<void> => {
    await apiClient.get("/dashboard/tech/carryoverdashboarddata");
  },
};
